import { useState } from 'react';
import { useTranslation } from 'react-i18next';

import { generateAbTestConfigRequestBody } from './ab-test-config/utils';
import ConfirmModal from 'src/components/email-builder/components/ConfirmModal';
import {
  useStartAbTestConfigMutation,
  useUpdateAbTestConfigMutation,
  useValidateAbTestConfigMutation,
} from 'src/services/agent-ab-test/agentAbTestApi';
import {
  SolveABTestConfig,
  VersionFreshnessIssue,
} from 'src/services/agent-ab-test/types';
import { setGlobalToastOptions } from 'src/slices/ui/uiSlice';
import { isForethoughtError } from 'src/slices/workflow-preview/types';
import { WorkflowVersionType } from 'src/slices/workflow-preview/types';
import { useAppDispatch } from 'src/store/hooks';

interface VersionStalenessModalProps {
  abTestConfig: SolveABTestConfig;
  freshnessIssues: VersionFreshnessIssue[];
  onClose: () => void;
  open: boolean;
}

/**
 * Builds the auto-fixed variants array for control_not_live issues.
 * Updates the control variant's version to the current live version.
 * Returns null if the issues cannot be auto-fixed (e.g. non_control_is_live).
 */
const buildAutoFixedVariants = (
  config: SolveABTestConfig,
  issues: VersionFreshnessIssue[],
) => {
  const hasBlockingIssue = issues.some(
    i => i.issue === 'non_control_is_live' || i.issue === 'no_live_version',
  );
  if (hasBlockingIssue) {
    return null;
  }

  const controlIssue = issues.find(i => i.issue === 'control_not_live');
  if (
    !controlIssue ||
    controlIssue.current_live_version === null ||
    controlIssue.current_live_version_type === null
  ) {
    return null;
  }

  const VALID_VERSION_TYPES: string[] = ['auto_saved', 'saved', 'published'];
  const liveVersion = controlIssue.current_live_version;
  const liveVersionType = controlIssue.current_live_version_type;
  if (!VALID_VERSION_TYPES.includes(liveVersionType)) {
    return null;
  }

  return (config.variants ?? []).map(v =>
    v.is_control
      ? {
          ...v,
          intent_workflow_version: liveVersion,
          version_type: liveVersionType as WorkflowVersionType,
        }
      : v,
  );
};

const VersionStalenessModal = ({
  abTestConfig,
  freshnessIssues,
  onClose,
  open,
}: VersionStalenessModalProps) => {
  const dispatch = useAppDispatch();
  const { t } = useTranslation('agent-ab-test');
  const [isProcessing, setIsProcessing] = useState(false);

  const [updateAbTestConfig] = useUpdateAbTestConfigMutation();
  const [validateAbTestConfig] = useValidateAbTestConfigMutation();
  const [startAbTest] = useStartAbTestConfigMutation();

  const autoFixedVariants = buildAutoFixedVariants(
    abTestConfig,
    freshnessIssues,
  );
  const canAutoFix = autoFixedVariants !== null;

  const handleUpdateAndRun = async () => {
    if (!autoFixedVariants) {
      return;
    }

    setIsProcessing(true);
    try {
      // 1. Update the config with corrected versions
      await updateAbTestConfig({
        abTestConfigId: abTestConfig.ab_test_config_id,
        body: generateAbTestConfigRequestBody(abTestConfig, {
          variants: autoFixedVariants,
        }),
      }).unwrap();

      // 2. Re-validate to catch any new issues
      const revalidation = await validateAbTestConfig(
        abTestConfig.ab_test_config_id,
      ).unwrap();

      if (
        !revalidation.is_valid ||
        (revalidation.version_freshness_issues?.length ?? 0) > 0
      ) {
        dispatch(
          setGlobalToastOptions({
            autoHideDuration: 5000,
            title: t('versionStalenessModal.toastVersionsChangedAgain'),
            variant: 'danger',
          }),
        );
        onClose();
        return;
      }

      // 3. Start the test
      await startAbTest(abTestConfig.ab_test_config_id).unwrap();
      dispatch(
        setGlobalToastOptions({
          autoHideDuration: 5000,
          title: t('versionStalenessModal.toastStartedWithUpdatedControl'),
          variant: 'main',
        }),
      );
      onClose();
    } catch (error) {
      dispatch(
        setGlobalToastOptions({
          autoHideDuration: 5000,
          title:
            typeof error === 'object' &&
            error !== null &&
            'data' in error &&
            isForethoughtError(error.data)
              ? error.data.message
              : t('versionStalenessModal.toastUpdateStartFailed'),
          variant: 'danger',
        }),
      );
      onClose();
    } finally {
      setIsProcessing(false);
    }
  };

  const description = (
    <>
      {freshnessIssues.map((issue, index) => (
        <p key={index}>{issue.message}</p>
      ))}
      {canAutoFix ? (
        <p>{t('versionStalenessModal.bodyCanAutoFix')}</p>
      ) : (
        <p>{t('versionStalenessModal.bodyBlockingIssue')}</p>
      )}
    </>
  );

  return (
    <ConfirmModal
      actions={
        canAutoFix
          ? [
              {
                onClick: onClose,
                title: t('versionStalenessModal.cancelButton'),
              },
              {
                isLoading: isProcessing,
                onClick: handleUpdateAndRun,
                title: t('versionStalenessModal.updateAndRunButton'),
              },
            ]
          : [
              {
                onClick: onClose,
                title: t('versionStalenessModal.goBackButton'),
              },
            ]
      }
      description={description}
      isOpen={open}
      onClose={onClose}
      title={t('versionStalenessModal.title')}
    />
  );
};

export default VersionStalenessModal;
